import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, type ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { BrandingProvider } from './branding/BrandingProvider.js';
import { Loading } from './shared/Loading.js';

/**
 * Route table and the bundle split it exists to create (§4, D-11).
 *
 * Every route is lazily imported, so a student who opens `/` downloads the
 * join screen and nothing else -- not the admin dashboard, and (from phase 6)
 * not twenty-nine exercise renderers they will never play. On classroom wifi
 * with thirty devices this is a requirement rather than an optimisation.
 */

const StudentEntry = lazy(() =>
  import('./play/StudentEntry.js').then((m) => ({ default: m.StudentEntry })),
);

const TeacherRemotePage = lazy(() =>
  import('./play/TeacherRemotePage.js').then((m) => ({ default: m.TeacherRemotePage })),
);

const PracticePage = lazy(() =>
  import('./practice/PracticePage.js').then((m) => ({ default: m.PracticePage })),
);

const LoginPage = lazy(() =>
  import('./admin/LoginPage.js').then((m) => ({ default: m.LoginPage })),
);
const GamesPage = lazy(() =>
  import('./admin/GamesPage.js').then((m) => ({ default: m.GamesPage })),
);
const GameBuilder = lazy(() =>
  import('./admin/GameBuilder.js').then((m) => ({ default: m.GameBuilder })),
);
const LobbyPage = lazy(() =>
  import('./admin/LobbyPage.js').then((m) => ({ default: m.LobbyPage })),
);
const SettingsPage = lazy(() =>
  import('./admin/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const UsersPage = lazy(() =>
  import('./admin/UsersPage.js').then((m) => ({ default: m.UsersPage })),
);
const AccountPage = lazy(() =>
  import('./admin/AccountPage.js').then((m) => ({ default: m.AccountPage })),
);
const ExerciseLabPage = lazy(() =>
  import('./admin/ExerciseLabPage.js').then((m) => ({ default: m.ExerciseLabPage })),
);
const AnalyticsPage = lazy(() =>
  import('./admin/AnalyticsPage.js').then((m) => ({ default: m.AnalyticsPage })),
);
const OrdabankiPage = lazy(() =>
  import('./admin/OrdabankiPage.js').then((m) => ({ default: m.OrdabankiPage })),
);
const InvitePage = lazy(() =>
  import('./admin/InvitePage.js').then((m) => ({ default: m.InvitePage })),
);
const BoardPlayground = lazy(() =>
  import('./dev/BoardPlayground.js').then((m) => ({ default: m.BoardPlayground })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The lobby's live data arrives over the socket, not by polling. Refetch
      // on focus would fight the socket for authority over the same state.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Outside the router: every route is branded, including the ones a
          student reaches without an account, and a provider mounted per route
          would refetch the logo on every navigation. */}
      <BrandingProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Students live at /spila for the whole lesson -- join form and
                  game are one route (StudentEntry). The bare root redirects
                  there so a typed leikur.multikulti.is still lands somewhere
                  useful. */}
              <Route path="/spila" element={<StudentEntry />} />
              <Route path="/kennari/:token" element={<TeacherRemotePage />} />
              <Route path="/" element={<Navigate to="/spila" replace />} />

              <Route path="/admin" element={<Navigate to="/admin/games" replace />} />
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin/games" element={<GamesPage />} />
              <Route path="/admin/games/new" element={<GameBuilder />} />
              <Route path="/admin/sessions/:sessionId" element={<LobbyPage />} />

              {/* White label and accounts (D-30, D-13). Superadmin-guarded on
                  the server; these pages only decide what to draw. */}
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/account" element={<AccountPage />} />
              <Route path="/admin/exercises" element={<ExerciseLabPage />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
              <Route path="/admin/ordabanki" element={<OrdabankiPage />} />

              {/* Reached from a link a superadmin copies out of the users page.
                  Unauthenticated by definition: the account does not exist
                  yet. */}
              <Route path="/admin/invite/:token" element={<InvitePage />} />

              {/* Self-study (D-10). Lazily imported like every other route, so a
                  player never downloads it and it never downloads the admin. */}
              <Route path="/leika" element={<PracticePage />} />

              {/* Offline projector sandbox for board CSS and token motion. */}
              <Route path="/dev/board" element={<BoardPlayground />} />

              <Route path="*" element={<Navigate to="/spila" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </BrandingProvider>
    </QueryClientProvider>
  );
}
